import { Tab, useMediaQuery, useTheme } from '@mui/material';
import { JSX, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch } from 'store/store';
import { getUserProfile } from 'store/user-service/asyncActions';

import { ROUTES } from 'core/constants/routes';
import { NamespacesList } from 'core/enums/common';
import { getEnvVarAsBoolean } from 'core/helpers/env-helper';
import { useGetUser } from 'core/hooks/useGetUser';

import { CustomTooltipElement } from '../shared/CustomDisabledElement/CustomTooltipElement';

import { Documents } from './Documents';
import { ModelSelection } from './ModelSelection';
import { Subscription } from './Subscription';
import { TabPanel } from './TabPanel';
import { User } from './User';
import * as Styled from './styled';

interface IProfileTabConfig {
	label: string;
	component: JSX.Element;
	styles: Record<string, string>;
	isSubscriptionOnly: boolean;
}

export const Profile = () => {
	const { t: profileTranslate } = useTranslation(NamespacesList['profilePage']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const dispatch = useAppDispatch();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isSmallMobile = useMediaQuery(theme.breakpoints.down('xs'));
	const location = useLocation();
	const navigate = useNavigate();

	const [tabIndex, setTabIndex] = useState(0);
	const [profileTabsConfig, setProfileTabsConfig] = useState<Array<IProfileTabConfig>>([]);

	const { user } = useGetUser();

	const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
		setTabIndex(newValue);
		
		// Navigation basée sur l'onglet sélectionné
		const isPaymentEnabled = getEnvVarAsBoolean('REACT_APP_ENABLE_PAYMENTS', true);
		
		// L'onglet 0 est toujours Profile (Settings)
		if (newValue === 0) {
			navigate(ROUTES.SETTINGS_PROFILE);
		} else if (newValue === 1 && isPaymentEnabled) {
			// L'onglet 1 est Subscription si les paiements sont activés
			navigate(ROUTES.SETTINGS_SUBSCRIPTION);
		}
	};

	useEffect(() => {
		const isPaymentEnabled = getEnvVarAsBoolean('REACT_APP_ENABLE_PAYMENTS', true);

		const tabs = [];

		tabs.push({
			label: profileTranslate('text.settings'),
			component: <User />,
			styles: { 
				maxWidth: isSmallMobile ? '100%' : isMobile ? '100%' : '574px', 
				margin: '0 auto',
				padding: isSmallMobile ? '0 12px' : isMobile ? '0 16px' : '0',
			},
			isSubscriptionOnly: false,
		});

		if (isPaymentEnabled) {
			tabs.push({
				label: profileTranslate('text.subscription'),
				component: <Subscription />,
				styles: {},
				isSubscriptionOnly: false,
			});
		}

		tabs.push({
			label: profileTranslate('text.documents'),
			component: <Documents />,
			styles: {},
			isSubscriptionOnly: true,
		});

		// AI Model tab hidden
		// tabs.push({
		// 	label: profileTranslate('text.aiModel'),
		// 	component: <ModelSelection />,
		// 	styles: {},
		// 	isSubscriptionOnly: true,
		// });

		setProfileTabsConfig(tabs);

		dispatch(getUserProfile());
	}, [isMobile, isSmallMobile, profileTranslate]);

	// Synchroniser l'onglet avec l'URL
	useEffect(() => {
		const isPaymentEnabled = getEnvVarAsBoolean('REACT_APP_ENABLE_PAYMENTS', true);

		if (location.pathname === ROUTES.SETTINGS_PROFILE) {
			setTabIndex(0);
		} else if (location.pathname === ROUTES.SETTINGS_SUBSCRIPTION && isPaymentEnabled) {
			setTabIndex(1);
		} else if (location.pathname === ROUTES.SETTINGS) {
			// Rediriger vers /settings/profile par défaut si on accède à /settings
			navigate(ROUTES.SETTINGS_PROFILE, { replace: true });
		}
	}, [location.pathname, navigate]);

	const userHasSubscription = useMemo(
		() => user?.accountPlan !== 0 && user?.accountPlan !== null,
		[user?.accountPlan]
	);

	return (
		<Styled.Profile>
			<Styled.TabsButtons value={tabIndex} onChange={handleChangeTab}>
				{profileTabsConfig.map((tab, index) =>
					!userHasSubscription && tab.isSubscriptionOnly ? (
						<CustomTooltipElement
							key={`tab page: ${index}`}
							message={commonTranslate('tooltips.unavailableForYourSubscriptionLevel')}
						>
							<Tab label={tab.label} sx={{ cursor: 'default' }} key={`tab: ${index}`} />
						</CustomTooltipElement>
					) : (
						<Tab label={tab.label} key={`tab: ${index}`} />
					)
				)}
			</Styled.TabsButtons>
			{profileTabsConfig.map((tab, index) => (
				<TabPanel key={`tab page: ${index}`} value={tabIndex} index={index}>
					<Styled.TabContent sx={tab.styles}>{tab.component}</Styled.TabContent>
				</TabPanel>
			))}
		</Styled.Profile>
	);
};
